// `capture_sessions` PUT dispatch for createSession.
//
// The adapter generates a UUID v4 client-side and INSERTs the
// optimistic session row locally; this connector POSTs the same id to
// /capture/sessions so the server's primary key matches.
//
// 4xx → throw (entry stays queued / surfaces to UI), with two exceptions:
// `already_exists` (below) and `failed_precondition` (the frozen-building
// drop — see the catch block):
//
//   FROZEN BUILDING (audit-1-#7 phase-2 residual). createSessionCore
//   rejects with failed_precondition when the building carries an
//   admin-only status (to_be_demolished / out_of_scope —
//   apps/backend/src/capture/create-session-freeze-gate.ts); it is the
//   ONLY failed_precondition cause on POST /capture/sessions. A stale
//   offline-queued session insert on a just-frozen building can never
//   land while the freeze holds, and failed_precondition stays TRANSIENT
//   in the central classifier (PERMANENT_SESSION_CODES omits it — the
//   PATCH LWW paths need the rethrow), so a bare rethrow here would
//   retry-wedge the whole device upload queue behind an entry the UI
//   reports as saved. The session holds no count of its own: the
//   dispatcher drains INLINE — drops the local orphan row (same shape as
//   the already_exists drop) and acks. Counts captured under the orphan
//   are preserved: their create-item POSTs 404 (the session never existed
//   server-side) and quarantine. Registry entry:
//   scripts/powersync-dispositions.mjs → connector-dispatch-session-insert.
//
//   `already_exists`:
//   The backend's idempotent-open logic (create-session.ts +
//   create-session-client-id.ts) rejects with 409 already_exists when
//   the user already has a session for the requested (user, scope) at a
//   DIFFERENT id. `details.authoritative` carries the real session
//   (a full SessionSummary; we read `id` + `completedAt`). Two flavours:
//
//   ACTIVE authoritative (completedAt === null) — the common single-
//   device race: the user's active session existed server-side but had
//   not synced down to THIS device's local SQLite before the offline
//   capture started, so the device minted a fresh local session id.
//   The orphan's CHILDREN — capture_items, and (via building lookup)
//   spaces/floors — were queued against this orphan id. If we only drop
//   the orphan (the historical behaviour) those children upload against
//   a session the server never had → 404 → the orphan-drop path silently
//   discards the surveyor's offline count. Asset counts are the
//   contractual baseline (AGENTS.md "Business criticality"), so instead
//   we RE-HOME the children onto the authoritative active session:
//     - record context.sessionIdRewrites (consumed by the capture_items
//       POST, which carries capture_session_id in its op data);
//     - record context.sessionRehomeByBuilding (consumed by
//       loadSessionIdForBuilding for the spaces/floors PUTs, which
//       recover the session from the building rather than op data);
//     - rewrite local ps_data__capture_items so the UI reflects the live
//       session before the next read-sync reconciles;
//   then drop the orphan. The re-homed children POST against the active
//   session and succeed — the count survives.
//
//   COMPLETED authoritative (completedAt !== null) — ALSO re-homed, as of
//   #664. The backend now accepts late create-item (#665) and create-space /
//   resolve-floor (#664) writes on a completed session, so re-homing the
//   children onto the completed authoritative LANDS them instead of wedging.
//   Before #664 this was a bare-drop (re-homing onto a completed session would
//   have 412-wedged the queue via the now-removed gates); that rationale no
//   longer holds. The 409 details carry the authoritative id directly, so the
//   dependent space/floor create resolves it WITHOUT waiting for the completed
//   row to sync down — closing the residual wedge Codex flagged on #669 (a
//   completed-session 409 whose authoritative wasn't yet in the local mirror).
//
//   A missing/malformed authoritative — no id, or completedAt absent entirely
//   (a shape the backend never emits) — still falls through to the bare
//   orphan-drop: there is no trustworthy target to re-home onto.
//
//   The captures sync stream delivers the authoritative session on the
//   next cycle; useActiveSession reconciles and the screen re-renders
//   against the real session.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { ApiError, apiFetch } from '../api'

import type { DispatchContext } from './connector-dispatch-context'
import { recordSessionRehome } from './connector-session-rehome-registry'
import { _emitUploaded } from './upload-events'

interface SessionUploadPostBody {
  id: string
  siteId: string
  buildingId: string | null
  sourceDeviceId: string | null
}

export async function dispatchSessionInsert(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
): Promise<void> {
  const data = entry.opData ?? {}

  const siteId = data['site_id']
  if (typeof siteId !== 'string' || siteId.length === 0) {
    throw new Error(
      `dispatchSessionInsert: capture_sessions row ${entry.id} is missing site_id; ` +
        `the local INSERT must populate it from the mutation.`,
    )
  }

  const rawBuildingId = data['building_id']
  const buildingId =
    typeof rawBuildingId === 'string' && rawBuildingId.length > 0
      ? rawBuildingId
      : null

  const rawSourceDeviceId = data['source_device_id']
  const sourceDeviceId =
    typeof rawSourceDeviceId === 'string' && rawSourceDeviceId.length > 0
      ? rawSourceDeviceId
      : null

  const body: SessionUploadPostBody = {
    id: entry.id,
    siteId,
    buildingId,
    sourceDeviceId,
  }

  try {
    await apiFetch(`/capture/sessions`, { method: 'POST', json: body })
    _emitUploaded(entry.table, entry.id)
  } catch (err) {
    if (err instanceof ApiError && err.code === 'already_exists') {
      const authoritative = readAuthoritativeTarget(err)
      if (authoritative !== null && authoritative.id !== entry.id) {
        // Active OR completed authoritative — re-home the orphan's children onto
        // the authoritative session so their uploads land instead of 404/wedge.
        // The completed case is now safe (and necessary): #664/#665 removed the
        // completed-session gates, so the children write fine; resolving the id
        // from the 409 details avoids waiting for the completed row to sync down
        // (Codex #669 finding #3). See the file header.
        context.sessionIdRewrites.set(entry.id, authoritative.id)
        // Also record in the DURABLE session re-home registry so a
        // complete/reopen the surveyor queued against the orphan id (offline,
        // possibly uploaded in a LATER CRUD batch OR after an app reload)
        // redirects onto the authoritative session — at its syncVersion — instead
        // of 404-ing and wedging the queue. Persisted to the local-only table
        // BEFORE the orphan is dropped/acked below. See
        // connector-session-rehome-registry.ts + dispatchSessionPatch.
        await recordSessionRehome(
          db,
          entry.id,
          authoritative.id,
          authoritative.syncVersion,
        )
        if (buildingId !== null) {
          context.sessionRehomeByBuilding.set(buildingId, authoritative.id)
        }
        // Direct ps_data__ write (no view trigger → no spurious CRUD op);
        // same precedent as the ps_data__buildings reopen cascade. Keeps
        // local capture_items pointing at the live session for the UI
        // until the read-sync delivers the authoritative rows.
        await db.execute(
          `UPDATE ps_data__capture_items
           SET data = json_set(data, '$.capture_session_id', ?)
           WHERE json_extract(data, '$.capture_session_id') = ?`,
          [authoritative.id, entry.id],
        )
        Sentry.captureMessage(
          '[powersync] capture_session re-homed to authoritative session',
          {
            level: 'info',
            extra: {
              reason: 'powersync_session_rehome',
              orphanId: entry.id,
              authoritativeId: authoritative.id,
              authoritativeCompleted: authoritative.completed,
              buildingId,
            },
          },
        )
        console.info(
          '[powersync] capture_sessions already_exists — re-homing children ' +
            'to authoritative session',
          {
            orphanId: entry.id,
            authoritativeId: authoritative.id,
            completed: authoritative.completed,
            buildingId,
          },
        )
      }
      // Drop the local orphan. Active case: children were just re-homed.
      // Completed/malformed case: historical bare-drop — the authoritative
      // session syncs down and reconciles. See the file header.
      await db.execute(`DELETE FROM capture_sessions WHERE id = ?`, [entry.id])
      return
    }
    if (err instanceof ApiError && err.code === 'failed_precondition') {
      // FROZEN BUILDING — see the file header. The only failed_precondition
      // cause on this endpoint is the admin-only-status freeze gate, so this
      // insert can never land; rethrowing would retry-wedge the whole device
      // queue (failed_precondition stays transient in the central classifier
      // for the PATCH LWW paths). Drain inline: drop the local orphan session
      // row and ack — its child counts quarantine on their own 404s.
      Sentry.captureMessage(
        '[powersync] capture_session insert rejected — building is frozen (admin-only status)',
        {
          level: 'info',
          extra: {
            reason: 'powersync_session_insert_frozen_building',
            orphanId: entry.id,
            buildingId,
          },
        },
      )
      console.info(
        '[powersync] capture_sessions failed_precondition — frozen building; ' +
          'dropping the local orphan session row (children quarantine on their own 404s)',
        { orphanId: entry.id, buildingId },
      )
      await db.execute(`DELETE FROM capture_sessions WHERE id = ?`, [entry.id])
      return
    }
    throw err
  }
}

/** The authoritative session a 409 points the orphan's children at. */
interface AuthoritativeTarget {
  readonly id: string
  /** Authoritative syncVersion (for a redirected complete/reopen), or null. */
  readonly syncVersion: number | null
  /** true when the authoritative session is already completed (completedAt set). */
  readonly completed: boolean
}

/**
 * Read the authoritative session's id + syncVersion from an `already_exists`
 * error. Returns the target for BOTH active (`completedAt === null`) and
 * completed (`completedAt` is a timestamp) authoritatives — re-homing onto a
 * completed session is safe as of #664/#665 (the completed-session create gates
 * were removed), so the children land instead of wedging (see the file header).
 * Returns null only when the authoritative is missing/malformed, carries no id,
 * or omits `completedAt` entirely (a shape the backend never emits — bare-drop
 * defensively). The backend attaches the full SessionSummary (including
 * syncVersion) via withDetails; `ApiError` preserves the raw
 * `{ code, message, details }` body on `err.body`.
 */
function readAuthoritativeTarget(err: ApiError): AuthoritativeTarget | null {
  const body = err.body
  const details =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)['details']
      : null
  const authoritative =
    typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>)['authoritative']
      : null
  if (typeof authoritative !== 'object' || authoritative === null) return null
  const row = authoritative as Record<string, unknown>
  const id = row['id']
  if (typeof id !== 'string' || id.length === 0) return null
  // A well-formed authoritative always carries completedAt (null = active, a
  // timestamp = completed). Its ABSENCE is a malformed/legacy shape we won't
  // re-home onto — bare-drop. Both null and a timestamp are re-homable.
  if (!('completedAt' in row)) return null
  const version = row['syncVersion']
  return {
    id,
    syncVersion: typeof version === 'number' ? version : null,
    completed: row['completedAt'] !== null,
  }
}
